import clsx from 'clsx'
import { Helmet } from "react-helmet"
import React, { useEffect, useState } from "react"
import { useLocation, useParams } from "react-router-dom";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";

import { IDAM } from "../../../api/Registry";
import { AttributeDialog } from "./AttributeDialog";
import { ErrorState } from "../../errors/ErrorState";
import { AttributeControls } from './AttributeControls';
import HttpServices from "../../../services/HttpServices";
import { LoadingXL } from "../../../components/utils/LoadingXL";
import { usePageContext } from "../../../lib/guards/PageContext";
import { ModuleBreadCrumb } from "../../../lib/fragments/ModuleBreadCrumb";
import { RouteHydration, getStandardRoute } from "../../../utils/Helpers";

export const EditUserAttribute = () => {
    const params = useParams()
    const location = useLocation()
    const locState = location.state
    const previousPath = locState?.previous_path ?? getStandardRoute('USER.ATTR_')

    const pageTitle = "Edit User Attribute"
    type ModalKeys = 'AttributeDialog' | ''
    const { updateDataContext } = usePageContext()

    const [state, setstate] = useState({
        posting: false,
        httpStatus: 200,
        status: 'pending',
        activeTab: 'controls',
        data: {
            attr: null,
            options: null,
        },
        breadCrumb: [
            { linkItem: false, title: 'IAM' },
            { linkItem: false, title: 'User Attributes' },
            { linkItem: false, title: locState?.title ?? pageTitle },
        ],
        show: {
            payload: null,
            AttributeDialog: false,
        },
        cargo: {
            action: 'create'
        },
    })

    const getAttributesDetails = async () => {
        let { httpStatus, status, data, breadCrumb } = state
        let helmetHeader = pageTitle

        try {
            const attributeDetailsRoute = [":attribute"]
                .reduce((route, param, index) =>
                    RouteHydration(route, param, [params.attribute][index]),
                    IDAM.USR_ATTR_ADDITION
                );

            const response: any = await HttpServices.httpGet(attributeDetailsRoute)
            httpStatus = response.status

            if (httpStatus === 200) {
                const payload: any = response.data.payload
                data.attr = payload.attr

                breadCrumb = breadCrumb.map((item, index) => {
                    if (index === breadCrumb.length - 1) {
                        return {
                            ...item,
                            title: data.attr.e
                        }
                    }
                    return item
                })

                status = 'fulfilled'
                helmetHeader = data.attr.s?.e
            } else {
                status = 'rejected'
            }
        } catch (error) {
            status = 'rejected'
            httpStatus = 500
        }

        setstate((prevState: any) => ({
            ...prevState,
            status,
            pageTitle,
            breadCrumb,
            httpStatus,
            data: {
                ...prevState.data,
            },
        }))

        dataContextHandler(status)
    }

    useEffect(() => {
        dataContextHandler()
        getAttributesDetails()
    }, [])

    const dataContextHandler = (status: string = 'pending', helmetHeader: string = pageTitle) => {
        const baseContext = {
            title: helmetHeader,
        }

        // Early return if data is not ready
        if (status !== 'fulfilled') {
            updateDataContext(baseContext)
            return
        }

        updateDataContext({
            ...baseContext,
            title: helmetHeader,
            backButton: previousPath,
        })
    }

    const toggleModal = (key: ModalKeys, visiblity: 'on' | 'off', payload?: any) => {
        setstate((prevState: any) => ({
            ...prevState,
            show: {
                [key]: visiblity === 'on',
                payload: visiblity === 'on' ? payload : null,
            },
        }))
    }

    const onCompleteActionHandler = () => {
        let { show } = state

        switch (show.payload?.action) {
            case 'attribute.amend':
                getAttributesDetails()
                break
        }
    }

    const activateTab = (tabName: any) => {
        setstate({
            ...state,
            activeTab: tabName
        })
    }

    const loadRespectiveTab = (tab: string = 'attributes') => {
        switch (tab) {
            case 'controls':
                return <AttributeControls
                    data={state.data}
                />

            default:
                return null
        }
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>

            <div className="flex flex-col gap-x-3 w-full mx-auto h-full">
                {
                    state.status === 'rejected' ? (
                        <div className="py-2 px-6 w-full mx-auto flex-1 flex flex-col justify-center">
                            <ErrorState
                                code={state.httpStatus}
                            />
                        </div>
                    ) : state.status === 'fulfilled' ? (
                        <>
                            <div className="w-full py-2 border-b border-slate-200 dark:border-slate-600 lg:px-6 px-5 flex flex-col">
                                <div className="flex-none">
                                    <ModuleBreadCrumb breadCrumb={state.breadCrumb} />
                                </div>
                            </div>

                            <div className="md:h-full lg:h-full flex flex-col lg:px-6 px-5 pt-4 pb-28">
                                <header className="w-full mb-2 header-title">
                                    <div className="flex md:flex-row flex-col align-middle items-center pb-2">
                                        <div className="flex-grow">
                                            <p className="text-2xl text-slate-900 dark:text-slate-100 font-medium truncate sm:tracking-normal flex flex-row align-middle items-center gap-x-3">
                                                {state.data.attr.s?.e}

                                                {
                                                    !state.data.attr.s?.n && (
                                                        <span className="bg-red-100 dark:bg-red-800/40 text-red-500 dark:text-red-400 rounded-md px-1.5 py-1 text-xs ring-0 ring-inset capitalize">
                                                            Disabled
                                                        </span>
                                                    )
                                                }
                                            </p>
                                        </div>

                                        <div className="flex-none flex flex-row">
                                            <span className="hidden sm:block">
                                                <button type="button" onClick={() => toggleModal('AttributeDialog', 'on', { action: 'attribute.amend' })} className="inline-flex items-center rounded-md bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/20 px-3 py-1.5 text-sm font- text-slate-900 dark:text-white shadow-xs ring-slate-300 inset-ring inset-ring-slate-300 dark:inset-ring-white/5 gap-x-1">
                                                    <svg viewBox="0 0 20 20" fill="currentColor" data-slot="icon" aria-hidden="true" className="mr-1.5 -ml-0.5 size-5 text-slate-400 dark:text-slate-300">
                                                        <path d="m2.695 14.762-1.262 3.155a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.886L17.5 5.501a2.121 2.121 0 0 0-3-3L3.58 13.419a4 4 0 0 0-.885 1.343Z" />
                                                    </svg>

                                                    Edit
                                                </button>
                                            </span>

                                            <span className="sm:ml-3">
                                                <button type="button" className="inline-flex items-center rounded-md bg-orange-500 px-3 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-orange-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600">
                                                    <svg viewBox="0 0 20 20" fill="currentColor" data-slot="icon" aria-hidden="true" className="mr-1.5 -ml-0.5 size-5">
                                                        <path d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" fillRule="evenodd" />
                                                    </svg>
                                                    Publish
                                                </button>
                                            </span>

                                            {/* Dropdown */}
                                            <Menu as="div" className="relative ml-3 sm:hidden">
                                                <MenuButton className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-xs inset-ring inset-ring-slate-300 hover:bg-slate-50">
                                                    More Actions
                                                    <svg viewBox="0 0 20 20" fill="currentColor" data-slot="icon" aria-hidden="true" className="-mr-1 ml-1.5 size-5 text-slate-400">
                                                        <path d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" fillRule="evenodd" />
                                                    </svg>
                                                </MenuButton>

                                                <MenuItems transition className="absolute left-0 z-10 mt-2 -mr-1 w-24 origin-top-right rounded-md bg-white py-1 shadow-lg outline outline-black/5 transition data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-75 data-leave:ease-in">
                                                    <MenuItem>
                                                        <a href="#" className="block px-4 py-2 text-sm text-slate-700 data-focus:bg-slate-100 data-focus:outline-hidden">
                                                            Edit
                                                        </a>
                                                    </MenuItem>

                                                    <MenuItem>
                                                        <a href="#" className="block px-4 py-2 text-sm text-slate-700 data-focus:bg-slate-100 data-focus:outline-hidden">
                                                            View
                                                        </a>
                                                    </MenuItem>
                                                </MenuItems>
                                            </Menu>
                                        </div>
                                    </div>

                                    <div className="w-full">
                                        {
                                            state.data.attr.s?.t ? (
                                                <p className="text-sm pb-3 text-slate-500 dark:text-slate-300">
                                                    {state.data.attr.s?.t ?? '-'}
                                                </p>
                                            ) : (
                                                <button type="button" onClick={() => toggleModal('AttributeDialog', 'on', { action: 'attribute.amend' })} className="text-sm pb-3 text-orange-500 hover:text-orange-600 dark:text-orange-500 dark:hover:text-orange-400">
                                                    Add a description
                                                </button>
                                            )
                                        }
                                    </div>
                                </header>

                                <div className="md:h-full lg:h-full flex flex-col">
                                    <div className="w-full border-b border-slate-200 dark:border-slate-600 lg:px-0 px-5 flex flex-row">
                                        <div className="md:flex-none cursor-pointer basis-1/2" onClick={() => activateTab('controls')}>
                                            <button className={clsx(
                                                state.activeTab === 'controls' ? 'text-slate-800 dark:text-orange-400 border-orange-400 border-b-2 dark:bg-orange-900/40' : 'hover:text-slate-700 hover:border-b-2 border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-900/50',
                                                "text-sm px-6 text-center w-full md:w-auto py-2 flex flex-row gap-x-2 align-middle items-center"
                                            )}>
                                                Controls
                                            </button>
                                        </div>

                                        <div className="md:flex-none cursor-pointer basis-1/2" onClick={() => activateTab('groups')}>
                                            <button className={clsx(
                                                state.activeTab === 'groups' ? 'text-slate-800 dark:text-orange-400 border-orange-400 border-b-2 dark:bg-orange-900/40' : 'hover:text-slate-700 hover:border-b-2 border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-900/50',
                                                "text-sm items-center block px-6 text-center w-full md:w-auto py-2"
                                            )}>
                                                <span className="lolrtn robot">
                                                    Selections
                                                </span>
                                            </button>
                                        </div>

                                        <div className="flex-grow md:block hidden">

                                        </div>
                                    </div>

                                    <div className="w-full h-full md:px-2 px-1.5 flex-1">
                                        {
                                            loadRespectiveTab(state.activeTab)
                                        }
                                    </div>
                                </div>




                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col justify-center w-full overflow-auto py-4">
                            <LoadingXL />
                        </div>
                    )
                }
            </div>

            <AttributeDialog
                dataDump={state.data}
                payload={state.show.payload}
                show={state.show.AttributeDialog}
                onComplete={onCompleteActionHandler}
                showOrHide={() => toggleModal('AttributeDialog', 'off')}
            />
        </React.Fragment>
    )
}